import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Add, CloudUpload, Delete, History, NetworkCheck, PlayArrow } from '@mui/icons-material'

import {
    useCreateExportMutation,
    useDeleteExportMutation,
    useListExportRunsQuery,
    useListExportsQuery,
    useRunExportMutation,
    useTestExportMutation,
    useUpdateExportMutation,
    useValidateExportMutation,
} from '@/apis/opsApi'
import type { WarehouseExport } from '@/apis/opsTypes'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { EmptyState } from '@/components/shared/EmptyState'
import { useProjectId } from '@/hooks/useReportArgs'
import { ReportHeader, ReportStatus, fmtDateTime, fmtNum } from '@/modules/reports/common'

import { ArchiveExportCard } from './ArchiveExportCard'
import { EMPTY_EXPORT_FORM, exportBody, type ExportForm } from './exportModel'

function destinationLabel(e: WarehouseExport): string {
    if (e.s3) {
        return `s3 · ${e.s3.bucket}/${e.s3.prefix}`
    }
    if (e.bigquery) {
        return `BigQuery · ${e.bigquery.project_id}.${e.bigquery.dataset}.${e.bigquery.table}`
    }
    return e.type
}

function RunsDialog({ exp, onClose }: { exp: WarehouseExport; onClose: () => void }) {
    const projectId = useProjectId()
    const { data, isLoading, error } = useListExportRunsQuery({ projectId, id: exp.id })
    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Runs · {exp.name}</DialogTitle>
            <DialogContent>
                <ReportStatus
                    isLoading={isLoading}
                    error={error}
                    isEmpty={!!data && data.length === 0}
                    emptyTitle="No runs yet"
                    emptyDescription="Exports run hourly; use Run now to start one."
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Started</TableCell>
                                <TableCell>Trigger</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Hours (UTC)</TableCell>
                                <TableCell align="right">Rows</TableCell>
                                <TableCell>Error</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data ?? []).map(r => (
                                <TableRow key={r.id}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {fmtDateTime(r.started_at)}
                                    </TableCell>
                                    <TableCell>{r.trigger}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={r.status}
                                            color={
                                                r.status === 'ok'
                                                    ? 'success'
                                                    : r.status === 'error'
                                                      ? 'error'
                                                      : 'default'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {r.from_hour ? `${r.from_hour} → ${r.to_hour ?? ''}` : '—'}
                                    </TableCell>
                                    <TableCell align="right">{fmtNum(r.rows)}</TableCell>
                                    <TableCell sx={{ maxWidth: 240 }}>{r.error ?? ''}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ReportStatus>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}

function ExportDialog({ onClose }: { onClose: () => void }) {
    const projectId = useProjectId()
    const dispatch = useAppDispatch()
    const [form, setForm] = useState<ExportForm>(EMPTY_EXPORT_FORM)
    const [formError, setFormError] = useState<string | null>(null)
    const [createExport, createState] = useCreateExportMutation()
    const [validate, validateState] = useValidateExportMutation()
    const set = (k: keyof ExportForm) => (e: { target: { value: string } }) =>
        setForm({ ...form, [k]: e.target.value })

    const body = (): Record<string, unknown> | null => {
        try {
            setFormError(null)
            return exportBody(form)
        } catch (err) {
            setFormError((err as Error).message)
            return null
        }
    }
    const test = async () => {
        const b = body()
        if (!b) {
            return
        }
        try {
            const r = await validate({ projectId, body: b }).unwrap()
            dispatch(
                showNotification({
                    message: r.ok ? `Connection OK: ${r.detail}` : `Connection failed: ${r.error}`,
                    severity: r.ok ? 'success' : 'error',
                }),
            )
        } catch {
            // shown from validateState.error
        }
    }
    const create = async () => {
        const b = body()
        if (!b) {
            return
        }
        try {
            await createExport({ projectId, body: b }).unwrap()
            onClose()
        } catch {
            // shown from createState.error
        }
    }
    const err = formError ?? v2ErrorMessage(createState.error ?? validateState.error)
    const s3 = form.type === 's3'
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>New warehouse export</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {err && <Alert severity="error">{err}</Alert>}
                <TextField
                    size="small"
                    label="Name"
                    value={form.name}
                    onChange={set('name')}
                    sx={{ mt: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        size="small"
                        select
                        label="Destination"
                        value={form.type}
                        onChange={set('type')}
                        fullWidth
                    >
                        <MenuItem value="s3">S3-compatible (S3, GCS, R2, MinIO)</MenuItem>
                        <MenuItem value="bigquery">BigQuery</MenuItem>
                    </TextField>
                    {s3 && (
                        <TextField
                            size="small"
                            select
                            label="Format"
                            value={form.format}
                            onChange={set('format')}
                            fullWidth
                        >
                            <MenuItem value="parquet">Parquet</MenuItem>
                            <MenuItem value="ndjson.gz">NDJSON (gzip)</MenuItem>
                        </TextField>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        size="small"
                        select
                        label="Schedule"
                        value={form.schedule}
                        onChange={set('schedule')}
                        fullWidth
                    >
                        <MenuItem value="hourly">Hourly</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                    </TextField>
                    <TextField
                        size="small"
                        type="date"
                        label="Backfill from (optional)"
                        value={form.start_date}
                        onChange={set('start_date')}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                    />
                </Box>
                {s3 ? (
                    <>
                        <TextField
                            size="small"
                            label="Endpoint"
                            required
                            placeholder="https://s3.eu-west-1.amazonaws.com"
                            value={form.endpoint}
                            onChange={set('endpoint')}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                size="small"
                                label="Bucket"
                                required
                                value={form.bucket}
                                onChange={set('bucket')}
                                fullWidth
                            />
                            <TextField
                                size="small"
                                label="Region"
                                value={form.region}
                                onChange={set('region')}
                                fullWidth
                            />
                        </Box>
                        <TextField
                            size="small"
                            label="Prefix"
                            value={form.prefix}
                            onChange={set('prefix')}
                        />
                        <TextField
                            size="small"
                            label="Access key id"
                            required
                            autoComplete="off"
                            value={form.access_key_id}
                            onChange={set('access_key_id')}
                        />
                        <TextField
                            size="small"
                            label="Secret access key"
                            required
                            type="password"
                            autoComplete="off"
                            value={form.secret_access_key}
                            onChange={set('secret_access_key')}
                        />
                    </>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                size="small"
                                label="GCP project"
                                required
                                value={form.project_id}
                                onChange={set('project_id')}
                                fullWidth
                            />
                            <TextField
                                size="small"
                                label="Location"
                                value={form.location}
                                onChange={set('location')}
                                fullWidth
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                size="small"
                                label="Dataset"
                                required
                                value={form.dataset}
                                onChange={set('dataset')}
                                fullWidth
                            />
                            <TextField
                                size="small"
                                label="Table"
                                value={form.table}
                                onChange={set('table')}
                                fullWidth
                            />
                        </Box>
                        <TextField
                            size="small"
                            label="Service account key (JSON)"
                            required
                            multiline
                            minRows={3}
                            value={form.service_account_json}
                            onChange={set('service_account_json')}
                            helperText="Needs BigQuery Job User on the project and Data Editor on the dataset."
                        />
                    </>
                )}
                <TextField
                    size="small"
                    label="Only this sub-tenant (optional)"
                    value={form.sub_tenant}
                    onChange={set('sub_tenant')}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    startIcon={<NetworkCheck />}
                    disabled={validateState.isLoading}
                    onClick={() => {
                        void test()
                    }}
                >
                    Test connection
                </Button>
                <Button
                    variant="contained"
                    disabled={createState.isLoading}
                    onClick={() => {
                        void create()
                    }}
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/** v2 `/exports`: hourly raw-event exports to the customer's own S3-compatible store or BigQuery. */
export function ExportsPage() {
    const projectId = useProjectId()
    const dispatch = useAppDispatch()
    const { data = [], isLoading, error } = useListExportsQuery(projectId)
    const [updateExport] = useUpdateExportMutation()
    const [deleteExport] = useDeleteExportMutation()
    const [testExport, testState] = useTestExportMutation()
    const [runExport, runState] = useRunExportMutation()
    const [creating, setCreating] = useState(false)
    const [runsFor, setRunsFor] = useState<WarehouseExport | null>(null)
    const [toDelete, setToDelete] = useState<WarehouseExport | null>(null)

    const notifyError = (err: unknown, fallback: string) =>
        dispatch(showNotification({ message: v2ErrorMessage(err) ?? fallback, severity: 'error' }))

    const test = async (e: WarehouseExport) => {
        try {
            const r = await testExport({ projectId, id: e.id }).unwrap()
            dispatch(
                showNotification({
                    message: r.ok ? `Connection OK: ${r.detail}` : `Connection failed: ${r.error}`,
                    severity: r.ok ? 'success' : 'error',
                }),
            )
        } catch (err) {
            notifyError(err, 'Test failed')
        }
    }
    const run = async (e: WarehouseExport) => {
        try {
            const r = await runExport({ projectId, id: e.id }).unwrap()
            dispatch(
                showNotification({
                    message:
                        r.status === 'noop'
                            ? 'Nothing new to export yet'
                            : `Run ${r.status}: ${fmtNum(r.rows)} rows over ${r.hours} hours${r.error ? ` (${r.error})` : ''}`,
                    severity: r.status === 'error' ? 'error' : 'success',
                }),
            )
        } catch (err) {
            notifyError(err, 'Run failed')
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Exports"
                subtitle="Download the raw event archive, or send your raw events hourly to your own bucket or BigQuery dataset."
                actions={
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setCreating(true)}
                    >
                        New export
                    </Button>
                }
            />
            <ArchiveExportCard />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Warehouse exports
            </Typography>
            <ReportStatus isLoading={isLoading} error={error}>
                {data.length === 0 ? (
                    <EmptyState
                        icon={<CloudUpload />}
                        title="No exports yet"
                        description="Export every event as Parquet or NDJSON to S3, GCS, R2 or BigQuery, including a backfill."
                        actionLabel="New export"
                        onAction={() => setCreating(true)}
                    />
                ) : (
                    <Card>
                        <CardContent>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name / destination</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Exported through (UTC hour)</TableCell>
                                        <TableCell>Last success</TableCell>
                                        <TableCell>Active</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map(e => (
                                        <TableRow key={e.id} hover>
                                            <TableCell sx={{ maxWidth: 340 }}>
                                                <Box sx={{ fontWeight: 600 }}>{e.name}</Box>
                                                <Box
                                                    sx={{
                                                        typography: 'caption',
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {destinationLabel(e)} · {e.schedule}
                                                    {e.sub_tenant ? ` · ${e.sub_tenant}` : ''}
                                                </Box>
                                                {(e.disabled_reason || e.last_error) && (
                                                    <Box
                                                        sx={{
                                                            typography: 'caption',
                                                            color: 'error.main',
                                                        }}
                                                    >
                                                        {e.disabled_reason ?? e.last_error}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={e.status}
                                                />
                                                {e.retry_hours.length > 0 && (
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        label={`${e.retry_hours.length} retrying`}
                                                        sx={{ ml: 0.5 }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {e.exported_through ??
                                                    `waiting for ${e.start_hour}`}
                                            </TableCell>
                                            <TableCell>
                                                {e.last_success_at
                                                    ? fmtDateTime(e.last_success_at)
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <FormControlLabel
                                                    label=""
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={e.active}
                                                            slotProps={{
                                                                input: {
                                                                    'aria-label': `Active: ${e.name}`,
                                                                },
                                                            }}
                                                            onChange={ev => {
                                                                void updateExport({
                                                                    projectId,
                                                                    id: e.id,
                                                                    body: {
                                                                        active: ev.target.checked,
                                                                    },
                                                                })
                                                            }}
                                                        />
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <Tooltip title="Run now">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Run ${e.name} now`}
                                                            disabled={runState.isLoading}
                                                            onClick={() => {
                                                                void run(e)
                                                            }}
                                                        >
                                                            <PlayArrow fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Test connection">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Test ${e.name}`}
                                                            disabled={testState.isLoading}
                                                            onClick={() => {
                                                                void test(e)
                                                            }}
                                                        >
                                                            <NetworkCheck fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Runs">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Runs of ${e.name}`}
                                                        onClick={() => setRunsFor(e)}
                                                    >
                                                        <History fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Delete ${e.name}`}
                                                        onClick={() => setToDelete(e)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
            </ReportStatus>
            {creating && <ExportDialog onClose={() => setCreating(false)} />}
            {runsFor && <RunsDialog exp={runsFor} onClose={() => setRunsFor(null)} />}
            <ConfirmDialog
                open={!!toDelete}
                title="Delete export?"
                message="Exports stop. Files already written stay in your storage."
                confirmLabel="Delete"
                confirmColor="error"
                onConfirm={() => {
                    if (toDelete) {
                        void deleteExport({ projectId, id: toDelete.id })
                    }
                    setToDelete(null)
                }}
                onCancel={() => setToDelete(null)}
            />
        </Box>
    )
}
